/*
 * Copyright (C) GMS Inc - All Rights Reserved
 * This file is part of GMSCloud. - Proprietary and confidential
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Created on   : 23-Jul-2026
 * @author      : Vincy R
 * Requirement# : 22299
 * Purpose      : Save-to targets multi-select ({loaded tenant, Provider default}) for the NAOMI mapping.
 */

import React from 'react';
import {
  Select, MenuItem, FormControl, InputLabel, Checkbox, ListItemText,
  OutlinedInput, Box, CircularProgress,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import type { TenantOption } from './services/TenantMappingService';
import { PROVIDER_SCOPE } from './MappingScopeSelect';

interface MappingSaveTargetsProps {
  /** The loaded tenant (the source), or null when the template was loaded from the provider. */
  tenant: TenantOption | null;
  /** Currently checked targets: a subset of { tenant.tenantId, PROVIDER_SCOPE }. */
  value: string[];
  loading?: boolean;
  onChange: (scopes: string[]) => void;
}

/**
 * Multi-select of Publish targets for the mapped task template. The ONLY permitted targets are the
 * loaded tenant itself and the Provider default — never other tenants (tenant-to-tenant sharing is
 * disallowed). Checking both saves the tenant's template to itself AND copies it up to the provider
 * in a single Publish. When the source is the provider, the tenant option is absent.
 */
export function MappingSaveTargets({ tenant, value, loading, onChange }: MappingSaveTargetsProps) {
  const handleChange = (e: SelectChangeEvent<string[]>) => {
    const v = e.target.value;
    onChange(typeof v === 'string' ? v.split(',') : v);
  };
  const labelFor = (scope: string) =>
    scope === PROVIDER_SCOPE ? 'Provider default' : (tenant?.tenantId === scope ? tenant.name : scope);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <FormControl size="small" sx={{ minWidth: 240 }}>
        <InputLabel id="mapping-save-targets-label">Save to</InputLabel>
        <Select<string[]>
          labelId="mapping-save-targets-label"
          multiple
          value={value}
          onChange={handleChange}
          disabled={loading}
          input={<OutlinedInput label="Save to" />}
          displayEmpty
          renderValue={(selected) =>
            selected.length
              ? selected.map(labelFor).join(', ')
              : <em style={{ color: '#888' }}>Select target(s)…</em>
          }
        >
          {tenant && (
            <MenuItem value={tenant.tenantId}>
              <Checkbox checked={value.includes(tenant.tenantId)} size="small" />
              <ListItemText primary={tenant.name} />
            </MenuItem>
          )}
          <MenuItem value={PROVIDER_SCOPE}>
            <Checkbox checked={value.includes(PROVIDER_SCOPE)} size="small" />
            <ListItemText primary="Provider default" />
          </MenuItem>
        </Select>
      </FormControl>
      {loading && <CircularProgress size={16} thickness={4} />}
    </Box>
  );
}
